<template>
  <v-text-field
    v-bind="attrs"
    :model-value="inputValue"
    type="text"
    inputmode="decimal"
    :disabled="disabled"
    :label="label"
    :prefix="currencyPrefix || undefined"
    @beforeinput="onBeforeInput"
    @update:model-value="onInput"
    @focus="isFocused = true"
    @blur="onBlur"
  />
</template>
<script lang="ts" setup>
import { computed, ref, useAttrs, watch } from "vue";

defineOptions({ inheritAttrs: false });

type AmountModelValue = number | null | undefined;

const attrs = useAttrs();
const emit = defineEmits<{
  (e: "update:modelValue", value: number | null): void;
}>();

const props = defineProps<{
  modelValue?: AmountModelValue;
  label?: string;
  disabled?: boolean;
  options?: Record<string, unknown>;
}>();

const inputValue = ref("");
const isFocused = ref(false);

const getCurrencyPrefix = (currency: string, locale?: string) => {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      currencyDisplay: "symbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(0);

    return parts.find((part) => part.type === "currency")?.value ?? currency;
  } catch {
    return currency;
  }
};

const currencyPrefix = computed(() => {
  const currency =
    typeof props.options?.currency === "string" ? props.options.currency.toUpperCase() : "";
  const locale = typeof props.options?.locale === "string" ? props.options.locale : undefined;

  if (!currency) {
    return "";
  }

  return getCurrencyPrefix(currency, locale);
});

const formatModelValue = (value: AmountModelValue): string => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return "";
  }

  return String(value);
};

const sanitizeInput = (value: string): string => {
  const normalizedSeparators = value.replace(/,/g, ".");
  const digitsAndDecimal = normalizedSeparators.replace(/[^\d.]/g, "");
  if (!digitsAndDecimal) {
    return "";
  }

  const [integerPart, ...decimalParts] = digitsAndDecimal.split(".");
  if (!decimalParts.length) {
    return integerPart;
  }

  return `${integerPart}.${decimalParts[0]}`;
};

const isValidCurrencyInput = (value: string) => {
  return /^\d*(\.\d{0,2})?$/.test(value);
};

const emitParsedValue = (value: string) => {
  if (!value || value === ".") {
    emit("update:modelValue", null);
    return;
  }

  const parsedValue = Number(value);
  emit("update:modelValue", Number.isFinite(parsedValue) ? parsedValue : null);
};

const onBeforeInput = (event: InputEvent) => {
  const input = event.target as HTMLInputElement | null;

  if (!input) {
    return;
  }

  if (event.inputType.startsWith("delete")) {
    return;
  }

  const insertedText = event.data ?? "";
  if (!insertedText) {
    return;
  }

  if (!/^[\d.,]+$/.test(insertedText)) {
    event.preventDefault();
    return;
  }

  const selectionStart = input.selectionStart ?? input.value.length;
  const selectionEnd = input.selectionEnd ?? selectionStart;
  const nextValue =
    input.value.slice(0, selectionStart) + insertedText + input.value.slice(selectionEnd);

  const rawNextValue = nextValue.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const decimalCount = (rawNextValue.match(/\./g) || []).length;

  if (decimalCount > 1) {
    event.preventDefault();
    return;
  }

  const sanitizedNextValue = sanitizeInput(rawNextValue);

  if (!isValidCurrencyInput(sanitizedNextValue)) {
    event.preventDefault();
  }
};

const onInput = (value: string) => {
  const sanitizedValue = sanitizeInput(String(value ?? ""));

  if (!isValidCurrencyInput(sanitizedValue)) {
    inputValue.value = formatModelValue(props.modelValue);
    return;
  }

  inputValue.value = sanitizedValue;
  emitParsedValue(sanitizedValue);
};

const onBlur = () => {
  isFocused.value = false;
  inputValue.value = formatModelValue(props.modelValue);
};

watch(
  () => props.modelValue,
  (value) => {
    if (isFocused.value) {
      return;
    }
    inputValue.value = formatModelValue(value);
  },
  { immediate: true },
);
</script>
